import { getLinkedinAccountById } from 'API/linkedin-accounts';
import { changeFunnelStatus, getFunnelById } from 'API/linkedin-funnel';
import { ErrorMessages } from 'enums';
import { Funnel, FunnelStatuses } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import _ from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { currentlyInWorkTime } from 'utils/date.utils';

interface Params {
  campaign: Funnel | null;
  linkedinAccounts: LinkedinAccount[];
  flowHasError: () => boolean;
  isStartCampaignPossible: (campaign: Funnel, linkedinAccounts: LinkedinAccount[]) => { isPossible: boolean; issues: string[] };
  setCampaign: Dispatch<SetStateAction<Funnel | null>>;
  setStartCampaignLoading: Dispatch<SetStateAction<boolean>>;
  handleOpenFlowIncompleteDialog: () => void;
  handleOpenFlowEmptyDialog: () => void;
  handleOpenStartingCampaignNotPossibleDialog: (issues: string[]) => void;
  handleOpenStartCampaignSuccessDialog: () => void;
  handleOpenStartMessageApprovalCampaignSuccessDialog: (campaign: Funnel, linkedinAccount: LinkedinAccount) => void;
  handleOpenLinkedinAccountNotFoundDialog: () => void;
  handleOpenCreditsExhaustedDialog: () => void;
  handleOpenCopilotLicenseExpiredDialog: () => void;
  handleOpenErrorDialog: (error: any, endpoint: string, method: string) => void;
  finishOnboarding: () => void;
}

const useCampaignStartFlow = ({
  campaign,
  linkedinAccounts,
  flowHasError,
  isStartCampaignPossible,
  setCampaign,
  setStartCampaignLoading,
  handleOpenFlowIncompleteDialog,
  handleOpenFlowEmptyDialog,
  handleOpenStartingCampaignNotPossibleDialog,
  handleOpenStartCampaignSuccessDialog,
  handleOpenStartMessageApprovalCampaignSuccessDialog,
  handleOpenLinkedinAccountNotFoundDialog,
  handleOpenCreditsExhaustedDialog,
  handleOpenCopilotLicenseExpiredDialog,
  handleOpenErrorDialog,
  finishOnboarding,
}: Params) => {
  const handleStartCampaign = async () => {
    if (_.isNil(campaign)) return;

    setStartCampaignLoading(true);

    const resCampaign = await getFunnelById(campaign._id);
    const resLinkedinAccount = await getLinkedinAccountById(campaign.linkedinAccount || '');

    if (!resCampaign.error) {
      setCampaign(resCampaign);
    } else {
      handleOpenErrorDialog(resCampaign?.error, '/campaigns/:id', 'get');
      setStartCampaignLoading(false);
      return;
    }

    if (flowHasError()) {
      handleOpenFlowIncompleteDialog();
      setStartCampaignLoading(false);
      return;
    }

    if (!_.isNil(resCampaign?.reactFlow)) {
      const { reactFlow } = resCampaign;

      if (_.isEmpty(reactFlow.nodes)) {
        handleOpenFlowEmptyDialog();
        setStartCampaignLoading(false);
        return;
      }
    }

    const { isPossible, issues } = isStartCampaignPossible(resCampaign, linkedinAccounts);

    if (!isPossible) {
      handleOpenStartingCampaignNotPossibleDialog(issues);
      setStartCampaignLoading(false);
      return;
    }

    const res = await changeFunnelStatus(campaign._id, FunnelStatuses.STARTED);

    if (!res.error) {
      setCampaign((prev) => ({ ...prev!, status: FunnelStatuses.STARTED }));
      const { workDays = [], workHours = [] } = resLinkedinAccount || {};
      localStorage.setItem('startedFunnelId', campaign._id);
      if (!(campaign.settings?.approveMessages ?? false) || currentlyInWorkTime(new Date(), workDays, workHours)) {
        handleOpenStartCampaignSuccessDialog();
      } else {
        handleOpenStartMessageApprovalCampaignSuccessDialog(resCampaign, resLinkedinAccount);
      }

      finishOnboarding();
    } else if (res.error.message === ErrorMessages.LINKEDIN_ACCOUNT_NOT_FOUND) {
      handleOpenLinkedinAccountNotFoundDialog();
    } else if (res.error.message === ErrorMessages.FUNNEL_CREDITS_EXHAUSTED) {
      handleOpenCreditsExhaustedDialog();
    } else if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
      handleOpenCopilotLicenseExpiredDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/campaigns/:id/status', 'post');
    }

    setStartCampaignLoading(false);
  };

  return { handleStartCampaign };
};

export default useCampaignStartFlow;
